import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import i18n from '@i18n/index';
import { DEFAULT_LOCALE } from '@i18n/locales';
import { captureApiException, clearPortalMonitoringIdentity } from '@lib/monitoring';

export const PORTAL_TOKEN_KEY = 'portal_session_token';
export const PORTAL_TOKEN_EXPIRES_AT_KEY = 'portal_session_token_expires_at';
export const PORTAL_CUSTOMER_EMAIL_KEY = 'portal_customer_email';

interface StoredPortalSessionInput {
  session_token: string;
  expires_at: string;
}

export const hasValidStoredPortalSession = () => {
  const token = localStorage.getItem(PORTAL_TOKEN_KEY);
  const expiresAt = localStorage.getItem(PORTAL_TOKEN_EXPIRES_AT_KEY);
  if (!token || !expiresAt) return false;

  const expiresAtTime = Date.parse(expiresAt);
  if (Number.isNaN(expiresAtTime)) return false;

  return expiresAtTime > Date.now();
};

export const persistStoredPortalSession = (auth: StoredPortalSessionInput) => {
  localStorage.setItem(PORTAL_TOKEN_KEY, auth.session_token);
  localStorage.setItem(PORTAL_TOKEN_EXPIRES_AT_KEY, auth.expires_at);
};

export const clearStoredPortalSession = () => {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
  localStorage.removeItem(PORTAL_TOKEN_EXPIRES_AT_KEY);
  localStorage.removeItem(PORTAL_CUSTOMER_EMAIL_KEY);
};

const axiosPortal = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosPortal.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem(PORTAL_TOKEN_KEY);
    if (token) {
      config.headers.set('Bearer', token);
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

axiosPortal.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: unknown) => {
    const err = error as { response?: { status: number } };
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      clearStoredPortalSession();
      clearPortalMonitoringIdentity();
      if (!window.location.pathname.includes('/login')) {
        window.location.href = `/${i18n.language || DEFAULT_LOCALE}/login`;
      }
    }
    captureApiException(error, 'portal');
    return Promise.reject(error);
  },
);

export default axiosPortal;
